import { useState, useMemo } from "react"
import { useDispatch } from "react-redux"
import { StoreDispatch } from "../../store/configure-store"
import { FromDropdown } from "../../cmps/forms/input/form-dropdown"
import { FormInputText } from "../../cmps/forms/input/form-input-text"
import { RequiredAsterisk } from "../../cmps/forms/required-asterisk"
import { Backlog } from "../../cmps/util-cmps/backlog"
import { Spinner } from "../../cmps/util-cmps/spinner"
import { UserMsg } from "../../models/user"
import { setUserMsg } from "../../store/user"
import { newHttpService } from "../../services/new-http-service"
import { updateObjectiveMutation } from "../../cmps/util-cmps/graphql-mutations/update-objective"
import { useEscapeKey } from "../../hooks/use-escape-key"
import { useScrollLock } from "../../hooks/use-scroll-lock"
import { useFocusTrap } from "../../hooks/use-focus-trap"
import { useDraggable } from "../../hooks/use-draggable"
import { SystemTypes, getRequiredSystems, setRequiredSystems } from "../forms/is-required-system"
import { parseAllDbsResult } from "../../cmps/util-cmps/parse-all-dbs-result"
import {
    buildObjectiveClipboardPayload,
    parseObjectiveClipboardPayload,
} from "./objective-clipboard"
import { getObjectiveCreateFlow } from "./objective-flow"
import { checkObjectiveSequence } from "./objective-sequence"

interface ObjectiveFormModalProps {
    objectives: any[]
    objectiveToEdit?: any
    onSuccess: () => void
    onClose: () => void
}

const getDistinctValues = (objectives: any[], field: string): string[] =>
    Array.from(new Set(objectives.map((o: any) => o[field]).filter(Boolean) as string[])).sort()

const getInitials = (name: string): string =>
    name.split(/\s+/).map(w => w[0]?.toUpperCase() || '').join('')

const getNextObjectiveNumber = (objectives: any[]): number => {
    const nums = objectives.map((o: any) => o.objectiveNumber).filter((n: any) => typeof n === 'number' && !isNaN(n))
    return nums.length > 0 ? Math.max(...nums) + 1 : 1
}

const getEmptyForm = (objectives: any[]) => ({
    name: '',
    objectiveNumber: String(getNextObjectiveNumber(objectives)),
    domain: '',
    domainAbbreviation: '',
    description: '',
    risk: '',
    frequency: '',
    type: '',
    itDependency: '',
    testType: '',
    specification: '',
    populationType: '',
    failureCriteria: '',
    testPlan: '',
    abbreviation: '',
    isKeyObjective: false,
})

const getFormFromObjective = (obj: any) => ({
    name: obj.name || '',
    objectiveNumber: String(obj.objectiveNumber ?? ''),
    domain: obj.domain || '',
    domainAbbreviation: obj.domainAbbreviation || '',
    description: obj.description || '',
    risk: obj.risk || '',
    frequency: obj.frequency || '',
    type: obj.type || '',
    itDependency: obj.ITDependency || '',
    testType: obj.testType || '',
    specification: obj.specification || '',
    populationType: obj.populationType || '',
    failureCriteria: obj.failureCriteria || '',
    testPlan: obj.testPlan || '',
    abbreviation: obj.abbreviation || '',
    isKeyObjective: Boolean(obj.isKeyObjective),
})

const BACKEND_ENV_NAME = (process.env.REACT_APP_BACKEND_ENVIRONMENT_NAME || '').toLowerCase()

const getWorkflowGuidance = (currentRegion: string, otherRegion: string, isPasteOnlyCreate: boolean) => {
    if (isPasteOnlyCreate) {
        return {
            title: 'US objective creation flow',
            intro: 'US is paste-only. Paste is always available, and Create verifies the US objective sequence before saving.',
            steps: [
                'Open the objective in EU and copy it to the clipboard.',
                'Switch to US and click Paste in this modal.',
                'Do not edit any field after paste.',
                'Click Create to run the sequence check and save the objective only if the next number is available.',
            ],
        }
    }

    return {
        title: 'Cross-region objective flow',
        intro: `Create the objective in ${currentRegion}, then copy it into ${otherRegion} when the other region also needs it.`,
        steps: [
            `Create the objective in ${currentRegion}.`,
            'Click Copy in this modal when you want to mirror the objective.',
            `Open the same modal in ${otherRegion} and click Paste.`,
            'Paste it as-is and save.',
        ],
    }
}

export const ObjectiveFormModal: React.FC<ObjectiveFormModalProps> = ({ objectives, objectiveToEdit, onSuccess, onClose }) => {
    const isEditMode = !!objectiveToEdit
    const objectiveCreateFlow = getObjectiveCreateFlow()
    const isPasteOnlyCreate = !isEditMode && objectiveCreateFlow.isPasteOnlyCreate
    const workflowGuidance = getWorkflowGuidance(
        objectiveCreateFlow.currentRegion,
        objectiveCreateFlow.otherRegion,
        isPasteOnlyCreate,
    )
    const dispatch = useDispatch<StoreDispatch>()
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [form, setForm] = useState(() => isEditMode ? getFormFromObjective(objectiveToEdit) : getEmptyForm(objectives))
    const [clipboardNotice, setClipboardNotice] = useState<{ kind: 'success' | 'danger'; txt: string } | null>(null)
    const [clipboardImported, setClipboardImported] = useState(false)
    const formLocked = isPasteOnlyCreate

    const objNum = parseInt(form.objectiveNumber, 10)
    const [requiredSystems, setRequiredSystemsState] = useState<string[]>(() => {
        if (isEditMode && !isNaN(objNum)) return getRequiredSystems(objNum)
        return [SystemTypes.IN_SCOPE]
    })
    const toggleRequiredSystem = (systemType: string) => {
        if (formLocked) return
        setRequiredSystemsState(prev => {
            if (systemType === SystemTypes.IN_SCOPE) return prev // Main is always required
            return prev.includes(systemType) ? prev.filter(s => s !== systemType) : [...prev, systemType]
        })
    }

    useEscapeKey(onClose)
    useScrollLock()
    const trapRef = useFocusTrap<HTMLDivElement>()
    const { modalRef, onHeaderMouseDown } = useDraggable<HTMLElement>()

    const domainOptions = useMemo(() => getDistinctValues(objectives, 'domain').map(v => ({ id: v, name: v })), [objectives])
    const frequencyOptions = useMemo(() => getDistinctValues(objectives, 'frequency').map(v => ({ id: v, name: v })), [objectives])
    const typeOptions = useMemo(() => getDistinctValues(objectives, 'type').map(v => ({ id: v, name: v })), [objectives])
    const itDependencyOptions = useMemo(() => getDistinctValues(objectives, 'ITDependency').map(v => ({ id: v, name: v })), [objectives])
    const testTypeOptions = useMemo(() => getDistinctValues(objectives, 'testType').map(v => ({ id: v, name: v })), [objectives])
    const specificationOptions = useMemo(() => getDistinctValues(objectives, 'specification').map(v => ({ id: v, name: v })), [objectives])
    const populationTypeOptions = useMemo(() => getDistinctValues(objectives, 'populationType').map(v => ({ id: v, name: v })), [objectives])

    const domainAbbreviationOptions = useMemo(() => {
        if (!form.domain) return []
        const abbrs = Array.from(new Set(
            objectives
                .filter((o: any) => o.domain === form.domain && o.domainAbbreviation)
                .map((o: any) => o.domainAbbreviation as string)
        )).sort()
        return abbrs.map(v => ({ id: v, name: v }))
    }, [objectives, form.domain])

    const handleDomainChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const domain = e.target.value
        const abbrs = Array.from(new Set(
            objectives
                .filter((o: any) => o.domain === domain && o.domainAbbreviation)
                .map((o: any) => o.domainAbbreviation as string)
        ))
        setForm(prev => ({
            ...prev,
            domain,
            domainAbbreviation: abbrs.length === 1 ? abbrs[0] : ''
        }))
    }

    const onChangeText = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (formLocked) return
        const { name, value } = e.target
        if (name === 'name') {
            const initials = getInitials(value)
            setForm(prev => prev.abbreviation === getInitials(prev.name)
                ? { ...prev, name: value, abbreviation: initials }
                : { ...prev, name: value }
            )
            return
        }
        setForm(prev => ({ ...prev, [name]: value }))
    }

    const onChangeDropdown = (e: React.ChangeEvent<HTMLSelectElement>) => {
        if (formLocked) return
        const { name, value } = e.target
        setForm(prev => ({ ...prev, [name]: value }))
    }

    const clearClipboardNotice = () => {
        setClipboardNotice(null)
    }

    const notifyClipboard = (kind: 'success' | 'danger', txt: string) => {
        setClipboardNotice({ kind, txt })
        dispatch(setUserMsg({ msg: { type: kind, txt } }))
    }

    const copyObjectiveToClipboard = async () => {
        clearClipboardNotice()
        try {
            const payload = buildObjectiveClipboardPayload(form, BACKEND_ENV_NAME || null, requiredSystems)
            const text = JSON.stringify(payload, null, 2)
            if (!navigator.clipboard?.writeText) {
                throw new Error('Clipboard write is not supported in this browser')
            }
            await navigator.clipboard.writeText(text)
            setClipboardImported(false)
            setClipboardNotice({ kind: 'success', txt: 'Objective copied to clipboard' })
        } catch (error) {
            const txt = error instanceof Error ? error.message : 'Failed to copy objective to clipboard'
            notifyClipboard('danger', txt)
        }
    }

    const pasteObjectiveFromClipboard = async () => {
        clearClipboardNotice()
        setClipboardImported(false)
        try {
            if (!navigator.clipboard?.readText) {
                throw new Error('Clipboard read is not supported in this browser')
            }
            const text = await navigator.clipboard.readText()
            const parsed = parseObjectiveClipboardPayload(text)
            if (isPasteOnlyCreate && (!parsed.isWrappedPayload || parsed.sourceEnv !== 'eu')) {
                throw new Error('US objective creation only accepts objectives copied from EU')
            }
            const pastedObjectiveNumber = parseInt(parsed.objective.objectiveNumber, 10)
            if (isNaN(pastedObjectiveNumber) || pastedObjectiveNumber <= 0) {
                throw new Error('Objective Number must be a positive integer')
            }
            const pastedObjective = parsed.objective
            setForm(pastedObjective)
            setRequiredSystemsState(parsed.objective.requiredSystems || getRequiredSystems(pastedObjectiveNumber))
            setClipboardImported(true)
            setClipboardNotice({
                kind: 'success',
                txt: isPasteOnlyCreate
                    ? `Objective pasted${parsed.sourceEnv ? ` from ${parsed.sourceEnv}` : ''}. Create will validate the US objective sequence before saving.`
                    : `Objective pasted${parsed.sourceEnv ? ` from ${parsed.sourceEnv}` : ''}. Review and adjust before saving.`,
            })
        } catch (error) {
            const txt = error instanceof Error ? error.message : 'Failed to paste objective from clipboard'
            notifyClipboard('danger', txt)
        }
    }

    const resetForm = () => {
        setForm(isEditMode ? getFormFromObjective(objectiveToEdit) : getEmptyForm(objectives))
        setClipboardImported(false)
        clearClipboardNotice()
        const baseNumber = isEditMode ? parseInt(String(objectiveToEdit?.objectiveNumber ?? ''), 10) : getNextObjectiveNumber(objectives)
        if (!isNaN(baseNumber)) {
            setRequiredSystemsState(getRequiredSystems(baseNumber))
        }
    }

    const existingObjNumbers = useMemo(() => new Set(objectives.map((o: any) => o.objectiveNumber)), [objectives])
    const existingAbbreviations = useMemo(() => new Set(objectives.map((o: any) => o.abbreviation?.toLowerCase()).filter(Boolean)), [objectives])

    const validate = (): string | null => {
        const trimmed = {
            name: form.name.trim(),
            objectiveNumber: form.objectiveNumber.trim(),
            description: form.description.trim(),
            risk: form.risk.trim(),
            testPlan: form.testPlan.trim(),
            failureCriteria: form.failureCriteria.trim(),
            abbreviation: form.abbreviation.trim(),
            domainAbbreviation: form.domainAbbreviation.trim(),
        }

        if (!trimmed.name) return 'Name is required'
        if (trimmed.name.length > 200) return 'Name exceeds 200 characters'

        const objNum = parseInt(trimmed.objectiveNumber, 10)
        if (!trimmed.objectiveNumber || isNaN(objNum) || objNum <= 0) return 'Objective Number must be a positive integer'
        const objNumChanged = !isEditMode || objNum !== objectiveToEdit.objectiveNumber
        if (objNumChanged && existingObjNumbers.has(objNum)) return `Objective Number ${objNum} already exists`

        if (!form.domain) return 'Domain is required'
        if (!trimmed.domainAbbreviation) return 'Domain Abbreviation is required'
        if (!trimmed.description) return 'Description is required'
        if (trimmed.description.length > 2000) return 'Description exceeds 2000 characters'
        if (!trimmed.risk) return 'Risk is required'
        if (trimmed.risk.length > 2000) return 'Risk exceeds 2000 characters'
        if (!form.frequency) return 'Frequency is required'
        if (!form.type) return 'Type is required'
        if (!form.itDependency) return 'IT Dependency is required'
        if (!form.testType) return 'Test Type is required'
        if (!form.specification) return 'Specification is required'
        if (!form.populationType) return 'Population Type is required'
        if (!trimmed.failureCriteria) return 'Failure Criteria is required'
        if (trimmed.failureCriteria.length > 2000) return 'Failure Criteria exceeds 2000 characters'
        if (!trimmed.testPlan) return 'Test Plan is required'
        if (trimmed.testPlan.length > 2000) return 'Test Plan exceeds 2000 characters'
        if (!trimmed.abbreviation) return 'Abbreviation is required'
        if (trimmed.abbreviation.length > 20) return 'Abbreviation exceeds 20 characters'
        if (!/^[A-Za-z0-9_-]+$/.test(trimmed.abbreviation)) return 'Abbreviation must be alphanumeric with hyphens/underscores only'
        const abbrChanged = !isEditMode || trimmed.abbreviation.toLowerCase() !== (objectiveToEdit.abbreviation || '').toLowerCase()
        if (abbrChanged && existingAbbreviations.has(trimmed.abbreviation.toLowerCase())) return `Abbreviation "${trimmed.abbreviation}" already exists`

        return null
    }

    const onSubmit = async () => {
        if (isPasteOnlyCreate && !clipboardImported) {
            dispatch(setUserMsg({ msg: { type: 'danger', txt: 'Paste an objective copied from EU before creating a US objective' } }))
            return
        }
        const error = validate()
        if (error) {
            const msg: UserMsg = { type: 'danger', txt: error }
            dispatch(setUserMsg({ msg }))
            return
        }

        setIsSubmitting(true)
        try {
            const parsedObjNum = parseInt(form.objectiveNumber, 10)
            if (isPasteOnlyCreate) {
                const sequenceStatus = await checkObjectiveSequence(parsedObjNum)
                if (!sequenceStatus.allowed) {
                    dispatch(setUserMsg({ msg: { type: 'danger', txt: sequenceStatus.message } }))
                    return
                }
            }
            if (isEditMode) {
                const query = updateObjectiveMutation()
                const variables = {
                    objectiveId: Number(objectiveToEdit.id),
                    name: form.name.trim(),
                    objectiveNumber: parsedObjNum,
                    domain: form.domain,
                    domainAbbreviation: form.domainAbbreviation.trim(),
                    description: form.description.trim(),
                    risk: form.risk.trim(),
                    frequency: form.frequency,
                    type: form.type,
                    itDependency: form.itDependency,
                    testType: form.testType,
                    specification: form.specification,
                    populationType: form.populationType,
                    failureCriteria: form.failureCriteria.trim(),
                    testPlan: form.testPlan.trim(),
                    abbreviation: form.abbreviation.trim(),
                    isKeyObjective: Boolean(form.isKeyObjective),
                }
                await newHttpService.graphql('auditech', { query, variables })
                setRequiredSystems(parsedObjNum, requiredSystems)
                const msg: UserMsg = { type: 'success', txt: 'Objective updated successfully' }
                dispatch(setUserMsg({ msg }))
                onSuccess()
            } else {
                const payload = {
                    name: form.name.trim(),
                    objective_number: parseInt(form.objectiveNumber, 10),
                    domain: form.domain,
                    domain_abbreviation: form.domainAbbreviation.trim(),
                    description: form.description.trim(),
                    risk: form.risk.trim(),
                    frequency: form.frequency,
                    type: form.type,
                    IT_dependency: form.itDependency,
                    test_type: form.testType,
                    specification: form.specification,
                    population_type: form.populationType,
                    failure_criteria: form.failureCriteria.trim(),
                    test_plan: form.testPlan.trim(),
                    abbreviation: form.abbreviation.trim(),
                    isKeyObjective: Boolean(form.isKeyObjective),
                }
                const res = await newHttpService.post('/api/backoffice/create-objective-all-dbs', payload)
                const msg: UserMsg = parseAllDbsResult(res.results || [], 'Objective')
                dispatch(setUserMsg({ msg }))
                if (msg.type !== 'danger') {
                    setRequiredSystems(parsedObjNum, requiredSystems)
                    onSuccess()
                }
            }
        } catch (error) {
            if (error instanceof Response) {
                try {
                    const payload = await error.json()
                    const txt = typeof payload?.error === 'string' && payload.error.trim()
                        ? payload.error
                        : isEditMode ? 'Failed updating objective' : 'Failed creating objective'
                    dispatch(setUserMsg({ msg: { type: 'danger', txt } }))
                    return
                } catch {
                    // Fall through to the generic error below.
                }
            }
            const msg: UserMsg = { type: 'danger', txt: isEditMode ? 'Failed updating objective' : 'Failed creating objective' }
            dispatch(setUserMsg({ msg }))
        } finally {
            setIsSubmitting(false)
        }
    }

    const modalTitle = isEditMode ? 'Edit Objective' : 'Create Objective'

    return <div ref={trapRef}>
        <section ref={modalRef} className="objective-form-modal" role="dialog" aria-modal="true" aria-label={modalTitle}>
            <header onMouseDown={onHeaderMouseDown}>
                <h2>{modalTitle}</h2>
                <div className="header-actions">
                    {!isPasteOnlyCreate && (
                        <button type="button" className="secondary-btn" onClick={copyObjectiveToClipboard}>
                            Copy
                        </button>
                    )}
                    <button type="button" className="secondary-btn" onClick={pasteObjectiveFromClipboard}>
                        Paste
                    </button>
                    {!isPasteOnlyCreate && (
                        <button type="button" className="secondary-btn" onClick={resetForm}>
                            Clear
                        </button>
                    )}
                    <button className="close-btn" onClick={onClose} aria-label="Close">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>
            </header>

            <div className="workflow-note">
                <div className="workflow-note-title">{workflowGuidance.title}</div>
                <p>{workflowGuidance.intro}</p>
                <ol>
                    {workflowGuidance.steps.map((step) => (
                        <li key={step}>{step}</li>
                    ))}
                </ol>
            </div>

            {isPasteOnlyCreate && !clipboardImported && (
                <div className="clipboard-note danger">
                    Paste the EU version before creating a US objective.
                </div>
            )}

            {clipboardNotice && (
                <div className={`clipboard-note ${clipboardNotice.kind}`}>
                    {clipboardNotice.txt}
                </div>
            )}

            <section className="modal-form-body">
                <div className="form-section-card">
                    <div className="form-section-label">General</div>
                    <div className="form-row">
                        <div className="field">
                            <FormInputText
                                keyName="name"
                                label="Name"
                                isRequired={true}
                                value={form.name}
                                onChange={onChangeText}
                                isDisabled={formLocked}
                            />
                        </div>
                        <div className="field">
                            <FormInputText
                                keyName="objectiveNumber"
                                label="Objective Number"
                                isRequired={true}
                                isNumberInput={true}
                                value={form.objectiveNumber}
                                onChange={onChangeText}
                                isDisabled={formLocked}
                            />
                            {isPasteOnlyCreate && <p className="form-info clipboard-review-note">Locked to the EU value after paste.</p>}
                        </div>
                    </div>
                </div>

                <div className="form-section-card">
                    <div className="form-section-label">Domain</div>
                    <div className="form-row">
                        <div className="field">
                            <FromDropdown
                                keyName="domain"
                                label="Domain"
                                options={domainOptions}
                                value={form.domain}
                                onSelectValue={handleDomainChange}
                                isDisabled={formLocked}
                            />
                        </div>
                        <div className="field">
                            <FromDropdown
                                keyName="domainAbbreviation"
                                label="Domain Abbreviation"
                                options={domainAbbreviationOptions}
                                value={form.domainAbbreviation}
                                onSelectValue={onChangeDropdown}
                                hideBlankOption={domainAbbreviationOptions.length > 0}
                                isDisabled={formLocked}
                            />
                        </div>
                    </div>
                </div>

                <div className="form-section-card">
                    <div className="form-section-label">Description</div>
                    <div className="textarea-field">
                        <label htmlFor="description">Description <RequiredAsterisk /></label>
                            <textarea
                                name="description"
                                id="description"
                                className="form-textarea"
                                value={form.description}
                                onChange={onChangeText}
                                rows={3}
                                maxLength={2000}
                                disabled={formLocked}
                            />
                    </div>
                    <div className="textarea-field">
                        <label htmlFor="risk">Risk <RequiredAsterisk /></label>
                            <textarea
                                name="risk"
                                id="risk"
                                className="form-textarea"
                                value={form.risk}
                                onChange={onChangeText}
                                rows={3}
                                maxLength={2000}
                                disabled={formLocked}
                            />
                    </div>
                </div>

                <div className="form-section-card">
                    <div className="form-section-label">Classification</div>
                    <div className="form-row three-col">
                        <div className="field">
                            <FromDropdown
                                keyName="frequency"
                                label="Frequency"
                                options={frequencyOptions}
                                value={form.frequency}
                                onSelectValue={onChangeDropdown}
                                isDisabled={formLocked}
                            />
                        </div>
                        <div className="field">
                            <FromDropdown
                                keyName="type"
                                label="Type"
                                options={typeOptions}
                                value={form.type}
                                onSelectValue={onChangeDropdown}
                                isDisabled={formLocked}
                            />
                        </div>
                        <div className="field">
                            <FromDropdown
                                keyName="itDependency"
                                label="IT Dependency"
                                options={itDependencyOptions}
                                value={form.itDependency}
                                onSelectValue={onChangeDropdown}
                                isDisabled={formLocked}
                            />
                        </div>
                    </div>
                    <div className="form-row three-col">
                        <div className="field">
                            <FromDropdown
                                keyName="testType"
                                label="Test Type"
                                options={testTypeOptions}
                                value={form.testType}
                                onSelectValue={onChangeDropdown}
                                isDisabled={formLocked}
                            />
                        </div>
                        <div className="field">
                            <FromDropdown
                                keyName="specification"
                                label="Specification"
                                options={specificationOptions}
                                value={form.specification}
                                onSelectValue={onChangeDropdown}
                                isDisabled={formLocked}
                            />
                        </div>
                        <div className="field">
                            <FromDropdown
                                keyName="populationType"
                                label="Population Type"
                                options={populationTypeOptions}
                                value={form.populationType}
                                onSelectValue={onChangeDropdown}
                                isDisabled={formLocked}
                            />
                        </div>
                    </div>
                </div>

                <div className="form-section-card">
                    <div className="form-section-label">Criteria</div>
                    <div className="textarea-field">
                        <label htmlFor="failureCriteria">Failure Criteria <RequiredAsterisk /></label>
                            <textarea
                                name="failureCriteria"
                                id="failureCriteria"
                                className="form-textarea"
                                value={form.failureCriteria}
                                onChange={onChangeText}
                                rows={3}
                                maxLength={2000}
                                disabled={formLocked}
                            />
                    </div>
                    <div className="textarea-field">
                        <label htmlFor="testPlan">Test Plan <RequiredAsterisk /></label>
                            <textarea
                                name="testPlan"
                                id="testPlan"
                                className="form-textarea"
                                value={form.testPlan}
                                onChange={onChangeText}
                                rows={3}
                                maxLength={2000}
                                disabled={formLocked}
                            />
                    </div>
                </div>

                <div className="form-section-card">
                    <div className="form-section-label">Identifier</div>
                    <div className="form-row">
                        <div className="field">
                            <FormInputText
                                keyName="abbreviation"
                                label="Abbreviation"
                                isRequired={true}
                                value={form.abbreviation}
                                onChange={onChangeText}
                                isDisabled={formLocked}
                            />
                        </div>
                    </div>
                </div>

                <div className="form-section-card">
                    <div className="form-section-label">Required Systems</div>
                    <p className="form-info">Select which system types are mandatory when creating audit controls for this objective.</p>
                    <div className="required-systems-checkboxes">
                        <label className="system-checkbox">
                            <input type="checkbox" checked={true} disabled />
                            <span>Main System (In Scope)</span>
                        </label>
                        <label className="system-checkbox">
                            <input type="checkbox" checked={requiredSystems.includes(SystemTypes.HR)} onChange={() => toggleRequiredSystem(SystemTypes.HR)} disabled={formLocked} />
                            <span>HR System</span>
                        </label>
                        <label className="system-checkbox">
                            <input type="checkbox" checked={requiredSystems.includes(SystemTypes.TICKET)} onChange={() => toggleRequiredSystem(SystemTypes.TICKET)} disabled={formLocked} />
                            <span>Ticket System</span>
                        </label>
                    </div>
                </div>

                <div className="form-actions">
                    {!isSubmitting && <button className="main" onClick={onSubmit} disabled={isSubmitting}>
                        {isEditMode ? 'Save' : 'Create'}
                    </button>}
                    {isSubmitting && <span className="spinner"><Spinner isSmall={true} /></span>}
                </div>
            </section>
        </section>
        <Backlog zIndex={199} onCloseBacklog={onClose} />
    </div>
}
